import React, { createContext, useContext, useState, useEffect } from 'react';
import toast from 'react-hot-toast';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

import storage from '../utils/storage';

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() => {
    const saved = storage.get('cartItems');
    if (!saved) return [];

    try {
      const items = JSON.parse(saved);
      // Sanitize items: ensure'size' is not an object
      return items.map(item => {
        if (item.size && typeof item.size === 'object') {
          return {
            ...item,
            size: item.size.size || null,
            color: item.size.color || item.color || null
          };
        }
        return item;
      });
    } catch (e) {
      return [];
    }
  });

  // Save to storage whenever cart changes
  useEffect(() => {
    storage.set('cartItems', JSON.stringify(cartItems));
  }, [cartItems]);

  const addToCart = (product, attributes = null, quantity = 1) => {
    let size = null;
    let color = null;

    if (attributes && typeof attributes === 'object') {
      size = attributes.size;
      color = attributes.color;
    } else {
      size = attributes;
    }

    const existingItem = cartItems.find(
      (item) =>
        item.productId === product._id &&
        item.size === size &&
        item.color === color
    );

    if (existingItem) {
      setCartItems(
        cartItems.map((item) =>
          item.productId === product._id && item.size === size && item.color === color
            ? { ...item, quantity: item.quantity + quantity }
            : item
        )
      );
    } else {
      setCartItems([
        ...cartItems,
        {
          productId: product._id,
          name: product.name,
          price: product.salePrice || product.price,
          image: product.images[0],
          size,
          color,
          quantity,
          category: product.category,
        },
      ]);
    }
    toast.success('Added to cart!');
    
    // Meta Pixel Tracking
    if (window.fbq) {
      window.fbq('track', 'AddToCart', {
        content_ids: [product._id],
        content_type: 'product',
        value: product.salePrice || product.price,
        currency: 'PKR'
      });
    }
  };

  const removeFromCart = (productId, size = null, color = null) => {
    setCartItems(
      cartItems.filter(
        (item) => !(item.productId === productId && item.size === size && item.color === color)
      )
    );
    toast.success('Removed from cart');
  };

  const updateQuantity = (productId, size, quantity, color = null) => {
    if (quantity <= 0) {
      removeFromCart(productId, size, color);
      return;
    }
    setCartItems(
      cartItems.map((item) =>
        item.productId === productId && item.size === size && item.color === color
          ? { ...item, quantity }
          : item
      )
    );
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const getCartTotal = () => {
    return cartItems.reduce(
      (total, item) => total + item.price * item.quantity,
      0
    );
  };

  const getCartCount = () => {
    return cartItems.reduce((count, item) => count + item.quantity, 0);
  };

  return (
    <CartContext.Provider
      value={{
        cartItems,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        getCartTotal,
        getCartCount,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
